import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import type { UserFeatures } from '@oscom/shared';

/**
 * Second navigation level: the areas of ONE customer.
 *
 * The header carries the platform wide areas; this carries the ones that only
 * make sense once a customer is open. Entries appear as the work packages that
 * build them land - a menu item that leads nowhere is the sham the fourth
 * ground rule forbids.
 */
export function CustomerContextNav({
  customerId,
  customerName,
  features,
}: {
  customerId: string;
  customerName: string;
  /**
   * `me.features` from the session. Typed as possibly absent on purpose: an
   * older API during a deploy skew sends no `features`, which must read as
   * "off" rather than crash the page.
   */
  features: UserFeatures | undefined;
}): ReactNode {
  const base = `/kunden/${encodeURIComponent(customerId)}`;
  const items = [
    { to: base, label: 'Stammdaten', end: true },
    { to: `${base}/orte`, label: 'Orte', end: false },
    ...(features?.technicalObjects === true
      ? [{ to: `${base}/objekte`, label: 'Objekte', end: false }]
      : []),
    ...(features?.measurements === true
      ? [{ to: `${base}/messungen`, label: 'Messungen', end: false }]
      : []),
    ...(features?.floorPlans === true
      ? [{ to: `${base}/plaene`, label: 'Pläne', end: false }]
      : []),
    ...(features?.documents === true
      ? [{ to: `${base}/dokumente`, label: 'Dokumente', end: false }]
      : []),
  ];

  return (
    <div className="context-nav">
      <p className="context-nav__title">{customerName}</p>
      <nav aria-label={`Bereiche von ${customerName}`}>
        <ul>
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  isActive ? 'context-nav__link is-active' : 'context-nav__link'
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
